import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Globe, Lock, CheckCircle, Users, Zap, Settings, RefreshCw, Mail, ChevronRight } from 'lucide-react';
import { SEO } from './SEO';

export const PrivacyView: React.FC = () => {
  const navigate = useNavigate();

  const styles = {
    sectionTitle: 'font-medium text-2xl text-brand-black mb-4 flex items-center gap-3',
    card: 'bg-white border border-brand-border rounded-xl shadow-sm p-8 mb-8',
    iconBg: 'p-2 bg-brand-surface text-brand-black border border-brand-border rounded-lg',
  };

  return (
    <>
      <SEO
        title="Privacy Policy - Five Dollar Workday"
        description="How Five Dollar Workday protects your data across all our products. Transparent privacy practices for developers."
      />
      
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-brand-black">
        
        {/* Hero */}
        <section className="py-12 mb-12 border-b border-brand-border bg-brand-surface -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <h1 className="text-4xl md:text-5xl tracking-tight leading-tight mb-4 font-semibold text-brand-black">
            Privacy Policy
          </h1>
          <p className="text-lg text-brand-charcoal font-light">
            Last Updated: <span className="font-medium text-brand-black">January 2026</span>
          </p>
        </section>

        {/* Introduction */}
        <section className="mb-8">
          <div className="p-6 border border-brand-border bg-white rounded-xl shadow-sm">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 flex-shrink-0 mt-0.5 text-brand-accent" />
              <div>
                <h2 className="font-medium text-xl text-brand-black mb-2">Our Privacy Commitment</h2>
                <p className="text-brand-charcoal font-light leading-relaxed">
                  Five Dollar Workday ("we," "us," or "our") is committed to protecting your privacy. We collect only what's necessary to provide our services and never sell your data to third parties.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Marketing Site Data Collection */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <Globe className="w-6 h-6" />
            </div>
            Marketing Site (fivedollar.web.app)
          </h2>
          
          <h3 className="font-medium text-brand-black mb-3">What We Collect:</h3>
          <ul className="space-y-2 mb-6 ml-5 text-brand-charcoal font-light">
            <li>• <strong className="text-brand-black font-medium">Account Information:</strong> When you create an account, we collect your email address and display name.</li>
            <li>• <strong className="text-brand-black font-medium">Usage Analytics:</strong> We use Google Analytics to understand how visitors use our site.</li>
            <li>• <strong className="text-brand-black font-medium">Authentication Data:</strong> If you sign in with Google, we receive your Google profile information.</li>
          </ul>

          <h3 className="font-medium text-brand-black mb-3">What We DON'T Collect:</h3>
          <ul className="space-y-2 mb-6 ml-5 text-brand-charcoal font-light">
            <li>• Payment information (we use Stripe, which handles all payment data securely)</li>
            <li>• Tracking cookies for advertising purposes</li>
            <li>• Any data beyond what's necessary for authentication and site functionality</li>
          </ul>
        </section>

        {/* Individual Product Privacy */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <CheckCircle className="w-6 h-6" />
            </div>
            Product-Specific Privacy Policies
          </h2>
          
          <p className="mb-6 text-brand-charcoal font-light">
            Each of our products has its own privacy policy that details the specific data collected and used for that product:
          </p>
          
          <div className="space-y-4">
            <a 
              href="https://assemblage-landing.web.app/privacy" 
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 border border-brand-border rounded-lg hover:border-brand-accent hover:shadow-md transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-brand-black group-hover:text-brand-accent transition-colors">Assemblage</h3>
                  <p className="text-sm text-brand-gray">Shopify inventory management app</p>
                </div>
                <ChevronRight className="w-5 h-5 text-brand-gray group-hover:text-brand-accent transition-colors" />
              </div>
            </a>

            <a 
              href="https://codetotext.app/privacy" 
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 border border-brand-border rounded-lg hover:border-brand-accent hover:shadow-md transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-brand-black group-hover:text-brand-accent transition-colors">CodeToText</h3>
                  <p className="text-sm text-brand-gray">Context for LLMs, minus the noise</p>
                </div>
                <ChevronRight className="w-5 h-5 text-brand-gray group-hover:text-brand-accent transition-colors" />
              </div>
            </a>
          </div>
        </section>

        {/* Data Security */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <Lock className="w-6 h-6" />
            </div>
            Data Security & Storage
          </h2>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• All data is encrypted in transit using TLS 1.3</li>
            <li>• All data is encrypted at rest using industry-standard encryption</li>
            <li>• Infrastructure hosted on Google Cloud Platform and Firebase</li>
            <li>• Regular security audits and updates</li>
            <li>• Access to user data is restricted to essential personnel only</li>
          </ul>
        </section>

        {/* Your Rights */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <Users className="w-6 h-6" />
            </div>
            Your Privacy Rights
          </h2>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li><strong className="text-brand-black font-medium">Access:</strong> Request a copy of your personal data</li>
            <li><strong className="text-brand-black font-medium">Correction:</strong> Request corrections to inaccurate data</li>
            <li><strong className="text-brand-black font-medium">Deletion:</strong> Request permanent account deletion</li>
            <li><strong className="text-brand-black font-medium">Portability:</strong> Request data in machine-readable format</li>
          </ul>
          
          <p className="mt-6 text-brand-charcoal font-light">
            To exercise any of these rights, contact us at <a href="mailto:fivedollarworkday@gmail.com" className="font-medium text-brand-accent hover:underline">fivedollarworkday@gmail.com</a>
          </p>
        </section>

        {/* Contact */}
        <section className="p-8 bg-brand-black rounded-xl text-white shadow-lg">
          <h2 className="text-2xl font-medium mb-4 flex items-center gap-3">
            <Mail className="w-6 h-6 text-brand-accent" />
            Questions About Privacy?
          </h2>
          
          <p className="mb-6 text-brand-gray font-light">
            If you have any questions about this privacy policy or our data practices, we're here to help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:fivedollarworkday@gmail.com" 
              className="inline-flex items-center justify-center px-6 py-3 font-medium bg-brand-accent text-white rounded-xl hover:bg-violet-700 transition-colors shadow-glow"
            >
              <Mail className="w-5 h-5 mr-2" />
              Contact Us
            </a>
            <button 
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-6 py-3 font-medium bg-white/10 text-white rounded-xl hover:bg-white/20 border border-white/20 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </section>

      </main>
    </>
  );
};